import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Button,
  Icon,
  Loader,
  Table,
  Form,
  Input,
  Grid,
  Header,
  Checkbox,
  Dropdown,
  Label,
  Popup,
} from 'semantic-ui-react';
import { clientService } from '../../services/clients/ClientService';
import type { ClientListResponse, ClientListFiltersRequest, ClienteLinhaDto } from '../../types/dtos';
import { useAuth } from '../../context/AuthContext';

const FLUXO_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  TEMPLATE_ENVIADO: 'Template enviado',
  AGUARDANDO_RESPOSTA: 'Aguardando',
  EM_FLUXO_AUTOMATIZADO: 'Em fluxo',
  ATENDIMENTO_HUMANO: 'Atendimento',
  FINALIZADO: 'Finalizado',
};

function PickerLinhasBadge({ linhas, acessosAtivos }: { linhas?: ClienteLinhaDto[] | null; acessosAtivos?: number | null }) {
  if (acessosAtivos == null && (!linhas || linhas.length === 0)) {
    return <span style={{ color: '#aaa' }}>—</span>;
  }
  const count = linhas?.length ?? acessosAtivos ?? 0;
  if (!linhas || linhas.length === 0) {
    return <Label size="tiny" color="grey">{acessosAtivos} linha(s)</Label>;
  }
  return (
    <Popup
      trigger={
        <Label size="tiny" color="blue" style={{ cursor: 'pointer' }}>
          <Icon name="mobile alternate" />{count}
        </Label>
      }
      content={
        <div style={{ maxWidth: 280 }}>
          {linhas.map((l) => (
            <div key={l.numeroLinha} style={{ marginBottom: 4 }}>
              <strong>{l.numeroLinha}</strong>
              {l.temWhatsApp === true && <Icon name="whatsapp" color="green" style={{ marginLeft: 4 }} />}
              {l.temWhatsApp === false && <Icon name="whatsapp" color="grey" style={{ marginLeft: 4 }} />}
              <div style={{ fontSize: '0.82em', color: '#666' }}>
                {l.plano}{l.statusLinha ? ` · ${l.statusLinha}` : ''}
              </div>
            </div>
          ))}
        </div>
      }
      on="hover"
      position="left center"
      wide
    />
  );
}

function PickerFluxoBadge({ status }: { status?: string | null }) {
  if (!status) return <span style={{ color: '#aaa' }}>—</span>;
  const label = FLUXO_LABEL[status] ?? status;
  const colorMap: Record<string, 'grey' | 'blue' | 'teal' | 'olive' | 'orange' | 'green'> = {
    PENDENTE: 'grey',
    TEMPLATE_ENVIADO: 'blue',
    AGUARDANDO_RESPOSTA: 'teal',
    EM_FLUXO_AUTOMATIZADO: 'olive',
    ATENDIMENTO_HUMANO: 'orange',
    FINALIZADO: 'green',
  };
  return <Label size="tiny" color={colorMap[status] ?? 'grey'}>{label}</Label>;
}

export interface ClientPickerModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (clients: ClientListResponse[]) => void;
}

const ClientPickerModal: React.FC<ClientPickerModalProps> = ({ open, onClose, onConfirm }) => {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.roles?.includes('Admin'));

  const [clients, setClients] = useState<ClientListResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    situacaoCadastral: '',
    cidade: '',
    estado: '',
    temContrato: 'all' as 'all' | 'sim' | 'nao',
    statusMeuTim: '' as '' | 'configurado' | 'nao-tem-acesso' | 'sem-linhas',
    temLinhasWhatsApp: 'all' as 'all' | 'sim' | 'nao',
    statusFluxo: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1,
  });
  /** Seleção persistente entre páginas */
  const [selectedById, setSelectedById] = useState<Map<string, ClientListResponse>>(() => new Map());

  const loadClients = useCallback(
    async (pageNumber: number, pageSize?: number, filterOverride?: typeof filters) => {
      const size = pageSize ?? pagination.pageSize;
      const fsrc = filterOverride ?? filters;
      const f: ClientListFiltersRequest = {
        search: fsrc.search.trim() || undefined,
        situacaoCadastral: fsrc.situacaoCadastral.trim() || undefined,
        cidade: fsrc.cidade.trim() || undefined,
        estado: fsrc.estado.trim() || undefined,
        pageNumber,
        pageSize: size,
      };
      if (fsrc.temContrato === 'sim') f.hasContract = true;
      else if (fsrc.temContrato === 'nao') f.hasContract = false;
      if (isAdmin && fsrc.statusMeuTim) f.meuTimStatus = fsrc.statusMeuTim;
      if (fsrc.temLinhasWhatsApp === 'sim') f.hasLinhasWhatsApp = true;
      else if (fsrc.temLinhasWhatsApp === 'nao') f.hasLinhasWhatsApp = false;
      if (fsrc.statusFluxo) f.statusFluxoWhatsApp = fsrc.statusFluxo;

      setLoading(true);
      try {
        const paged = await clientService.getClientsPaged(f);
        setClients(paged.items);
        setPagination({
          currentPage: paged.currentPage,
          pageSize: paged.pageSize,
          totalCount: paged.totalCount,
          totalPages: paged.totalPages,
        });
      } catch (e) {
        console.error(e);
        setClients([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, isAdmin, pagination.pageSize]
  );

  const initialFiltersRef = useRef({
    search: '',
    situacaoCadastral: '',
    cidade: '',
    estado: '',
    temContrato: 'all' as 'all' | 'sim' | 'nao',
    statusMeuTim: '' as '' | 'configurado' | 'nao-tem-acesso' | 'sem-linhas',
    temLinhasWhatsApp: 'all' as 'all' | 'sim' | 'nao',
    statusFluxo: '',
  });

  useEffect(() => {
    if (!open) return;
    setSelectedById(new Map());
    const init = { ...initialFiltersRef.current };
    setFilters(init);
    void loadClients(1, 20, init);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abrir modal = reset + 1ª página com filtros limpos
  }, [open]);

  const handleSearch = () => void loadClients(1);

  const allOnPageSelected =
    clients.length > 0 && clients.every((c) => selectedById.has(c.id));

  const toggleSelectAllOnPage = () => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        clients.forEach((c) => next.delete(c.id));
      } else {
        clients.forEach((c) => next.set(c.id, c));
      }
      return next;
    });
  };

  const toggleRow = (c: ClientListResponse) => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (next.has(c.id)) next.delete(c.id);
      else next.set(c.id, c);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedById.values()));
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeOnDimmerClick={false}>
      <Modal.Header>
        <Icon name="users" />
        Selecionar clientes para a campanha
      </Modal.Header>
      <Modal.Content scrolling>
        <p style={{ marginBottom: '16px', color: '#555' }}>
          Os filtros seguem o cadastro principal. Você pode marcar clientes em várias páginas antes de confirmar.
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <Grid stackable columns={2}>
            <Grid.Column>
              <Form.Field>
                <label>Buscar</label>
                <Input
                  placeholder="CNPJ, razão social, e-mail…"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  icon="search"
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Situação cadastral</label>
                <Input
                  placeholder="Ex.: ATIVA"
                  value={filters.situacaoCadastral}
                  onChange={(e) => setFilters({ ...filters, situacaoCadastral: e.target.value })}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Cidade</label>
                <Input
                  value={filters.cidade}
                  onChange={(e) => setFilters({ ...filters, cidade: e.target.value })}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>UF</label>
                <Input
                  placeholder="PR"
                  value={filters.estado}
                  onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Tem contrato</label>
                <Dropdown
                  selection
                  value={filters.temContrato}
                  options={[
                    { key: 'all', value: 'all', text: 'Todos' },
                    { key: 'sim', value: 'sim', text: 'Sim' },
                    { key: 'nao', value: 'nao', text: 'Não' },
                  ]}
                  onChange={(_, d) =>
                    setFilters({ ...filters, temContrato: (d.value as 'all' | 'sim' | 'nao') ?? 'all' })
                  }
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Tem linhas WhatsApp</label>
                <Dropdown
                  selection
                  value={filters.temLinhasWhatsApp}
                  options={[
                    { key: 'all', value: 'all', text: 'Todos' },
                    { key: 'sim', value: 'sim', text: 'Sim' },
                    { key: 'nao', value: 'nao', text: 'Não' },
                  ]}
                  onChange={(_, d) =>
                    setFilters({ ...filters, temLinhasWhatsApp: (d.value as 'all' | 'sim' | 'nao') ?? 'all' })
                  }
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Status Fluxo WA</label>
                <Dropdown
                  selection
                  clearable
                  placeholder="Qualquer"
                  value={filters.statusFluxo || undefined}
                  options={[
                    { key: 'p', value: 'PENDENTE', text: 'Pendente' },
                    { key: 'te', value: 'TEMPLATE_ENVIADO', text: 'Template enviado' },
                    { key: 'ar', value: 'AGUARDANDO_RESPOSTA', text: 'Aguardando' },
                    { key: 'ef', value: 'EM_FLUXO_AUTOMATIZADO', text: 'Em fluxo' },
                    { key: 'ah', value: 'ATENDIMENTO_HUMANO', text: 'Atendimento' },
                    { key: 'f', value: 'FINALIZADO', text: 'Finalizado' },
                  ]}
                  onChange={(_, d) =>
                    setFilters({ ...filters, statusFluxo: (d.value as string) ?? '' })
                  }
                />
              </Form.Field>
            </Grid.Column>
            {isAdmin && (
              <Grid.Column>
                <Form.Field>
                  <label>Meu TIM (admin)</label>
                  <Dropdown
                    selection
                    clearable
                    placeholder="Qualquer"
                    value={filters.statusMeuTim || undefined}
                    options={[
                      { key: 'cfg', value: 'configurado', text: 'Configurado' },
                      { key: 'nac', value: 'nao-tem-acesso', text: 'Não tem acesso' },
                      { key: 'sem', value: 'sem-linhas', text: 'Sem linhas' },
                    ]}
                    onChange={(_, d) =>
                      setFilters({
                        ...filters,
                        statusMeuTim: (d.value as typeof filters.statusMeuTim) ?? '',
                      })
                    }
                  />
                </Form.Field>
              </Grid.Column>
            )}
          </Grid>
          <Button type="submit" primary icon labelPosition="left">
            <Icon name="search" />
            Buscar
          </Button>
        </Form>

        {loading ? (
          <Loader active style={{ marginTop: '24px' }} content="Carregando clientes…" />
        ) : (
          <>
            <Header as="h4" style={{ marginTop: '20px' }}>
              Resultados ({clients.length} nesta página)
            </Header>
            {clients.length === 0 ? (
              <p style={{ color: '#888' }}>Nenhum cliente nesta página. Ajuste os filtros e busque novamente.</p>
            ) : (
              <Table celled compact selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell collapsing>
                      <Checkbox
                        checked={allOnPageSelected}
                        indeterminate={
                          !allOnPageSelected && clients.some((c) => selectedById.has(c.id))
                        }
                        onChange={() => toggleSelectAllOnPage()}
                      />
                    </Table.HeaderCell>
                    <Table.HeaderCell>Empresa</Table.HeaderCell>
                    <Table.HeaderCell>CNPJ</Table.HeaderCell>
                    <Table.HeaderCell>Telefone</Table.HeaderCell>
                    <Table.HeaderCell>Cidade / UF</Table.HeaderCell>
                    <Table.HeaderCell>Linhas</Table.HeaderCell>
                    <Table.HeaderCell>Fluxo WA</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {clients.map((c) => (
                    <Table.Row key={c.id} onClick={() => toggleRow(c)} style={{ cursor: 'pointer' }}>
                      <Table.Cell
                        collapsing
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedById.has(c.id)}
                          onChange={() => toggleRow(c)}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <strong>{c.razaoSocial}</strong>
                      </Table.Cell>
                      <Table.Cell>{c.cnpj}</Table.Cell>
                      <Table.Cell>{c.telefone1 || '—'}</Table.Cell>
                      <Table.Cell>
                        {c.cidade} / {c.estado}
                      </Table.Cell>
                      <Table.Cell collapsing>
                        <PickerLinhasBadge linhas={c.linhas} acessosAtivos={c.acessosAtivos} />
                      </Table.Cell>
                      <Table.Cell collapsing>
                        <PickerFluxoBadge status={c.statusFluxoWhatsApp} />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            <div
              style={{
                marginTop: '12px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: '0.9em' }}>
                Página {pagination.currentPage} / {pagination.totalPages} — Total cadastro:{' '}
                {pagination.totalCount}
              </span>
              <Button.Group size="small">
                <Button
                  icon
                  disabled={pagination.currentPage <= 1}
                  onClick={() => void loadClients(pagination.currentPage - 1)}
                >
                  <Icon name="angle left" />
                </Button>
                <Button
                  icon
                  disabled={pagination.currentPage >= pagination.totalPages}
                  onClick={() => void loadClients(pagination.currentPage + 1)}
                >
                  <Icon name="angle right" />
                </Button>
              </Button.Group>
            </div>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <span style={{ float: 'left', lineHeight: '36px', marginLeft: '8px' }}>
          <strong>{selectedById.size}</strong> cliente(s) selecionado(s)
        </span>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          primary
          onClick={handleConfirm}
          disabled={selectedById.size === 0}
          icon="checkmark"
          labelPosition="left"
          content="Confirmar"
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ClientPickerModal;
